/**
 * useContentTags Hook
 *
 * Feature-specific hook for fetching and managing media content by hashtag/content tag.
 * Part of clean architecture: Component → Hook → UseCase → Service
 *
 * Pattern mirrors useUserMedia.ts:
 * - Encapsulates business logic for content-by-tag fetching
 * - Manages local state for medias, pagination, loading
 * - Does NOT use React Query (component-level state only)
 * - Returns actions and state for the UI
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { createFetchContentByTagUseCase } from '@/domain/usecases/contentTag/fetchContentByTagUseCase';
import { selectHasActiveSubscription } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';
import type { RootState } from '@/presentation/store';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { PageCursor } from '@/data/types/json';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseContentTagsResult {
    medias: UserMediaEntity[];
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    error: Error | null;
    fetchMedias: () => Promise<void>;
    loadMore: () => Promise<void>;
    refresh: () => Promise<void>;
}

export const useContentTags = (
    tagName: string,
    isHashtag: boolean,
    limit: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
): UseContentTagsResult => {
    const { l10n } = useLocale();
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const store = useStore<RootState>();
    const hasActiveSubscription = useCallback(
        (creatorUserId: string) => selectHasActiveSubscription(store.getState(), creatorUserId),
        [store],
    );
    const [medias, setMedias] = useState<UserMediaEntity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef(false);

    useEffect(() => {
        isLoadingMoreRef.current = isLoadingMore;
    }, [isLoadingMore]);

    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [lastFetchedItem, setLastFetchedItem] = useState<PageCursor | undefined>();

    const fetchMedias = useCallback(async () => {
        if (!tagName) {
            setError(new Error(l10n.contentTags.tagNameRequired));
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const useCase = createFetchContentByTagUseCase();
            const result = await useCase.execute(tagName, isHashtag, currentUser, hasActiveSubscription, undefined, limit);

            setMedias(result.medias);
            setHasMore(result.hasMore);
            setLastFetchedItem(result.cursor);
        } catch (err) {
            appLogger.error('Error fetching medias', 'useContentTags.fetchMedias', { error: err as Error });
            setError(err instanceof Error ? err : new Error(l10n.contentTags.fetchFailed));
            setMedias([]);
        } finally {
            setIsLoading(false);
        }
    }, [tagName, isHashtag, currentUser, hasActiveSubscription, limit]);

    const loadMore = useCallback(async () => {
        if (!tagName || !hasMore || isLoadingMoreRef.current) {
            return;
        }

        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;

        setError(null);

        try {
            const useCase = createFetchContentByTagUseCase();
            const result = await useCase.execute(tagName, isHashtag, currentUser, hasActiveSubscription, lastFetchedItem, limit);

            setMedias(prev => [...prev, ...result.medias]);
            setHasMore(result.hasMore);
            setLastFetchedItem(result.cursor);
        } catch (err) {
            appLogger.error('Error loading more medias', 'useContentTags.loadMore', { error: err as Error });
            setError(err instanceof Error ? err : new Error(l10n.contentTags.loadMoreFailed));
        } finally {
            setIsLoadingMore(false);
            isLoadingMoreRef.current = false;
        }
    }, [tagName, isHashtag, currentUser, hasActiveSubscription, hasMore, lastFetchedItem, limit]);

    // Keep fetched medias in sync with global events
    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
            setMedias(prev => prev.filter(m => m.id !== mediaId));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, likes } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, comments } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, ...patch } : m));
        });
    }, []);

    const refresh = useCallback(async () => {
        setMedias([]);
        setLastFetchedItem(undefined);
        setHasMore(false);
        await fetchMedias();
    }, [fetchMedias]);

    return {
        medias,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        fetchMedias,
        loadMore,
        refresh,
    };
};
